import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import { assertNever, filterNonNull, getDataOrNull } from "@msys/common";
import {
  CardContainer,
  CollapseSection,
  LabeledValue,
  LabeledValueWithButton,
  MenuButton,
  MenuItemWithIcon,
  ModalOpenButton,
  TruncatedHtmlValue,
  ValueWithDiff,
  VisibilityIndicatorIcon,
} from "@msys/ui";
import {
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Functions as FunctionsIcon,
  Search as SearchIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Icon,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikConfig, useFormik } from "formik";
import {
  capitalize,
  differenceWith,
  isNull,
  isUndefined,
  noop,
  partition,
} from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { useOutletContext } from "react-router-dom";
import * as Yup from "yup";
import {
  DefineItemProductSearchFilterDefinitionsInput,
  DocType,
  EntitySearchBoolFilterOperator,
  EntitySearchBoolInFilterOperator,
  EntitySearchNumberBetweenFilterOperator,
  EntitySearchFilterOperator,
  EntitySearchNumberFilterOperator,
  EntitySearchNumberInFilterOperator,
  EntitySearchTextFilterOperator,
  EntitySearchTextInFilterOperator,
  ModifyItemProductValuesInput,
  namedOperations,
  PermissionName,
  PimSupplierProductStatusValue,
  Props2NonComputed,
  Visibility,
} from "../../../../clients/graphqlTypes.js";
import SapLogo from "../../../assets/icons/sap-logo.svg?react";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import {
  type DocumentByType,
  RestrictedByDocumentPermissionByTypeWithDebug,
} from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { ManualSave } from "../../../commons/form-fields/ManualSave.js";
import { RichTextField } from "../../../commons/form-fields/RichTextField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import type { ViewMode } from "../../../commons/ViewModeMenuItem.js";
import { ViewModeMenuItem } from "../../../commons/ViewModeMenuItem.js";
import { VisibilityButton } from "../../../commons/VisibilityButton.js";
import { ALLOW_AI_FEATURE } from "../../../constants.js";
import { useSelectCrmSuppliersQuery } from "../../crm/CrmSuppliersSelect.generated.js";
import { ItemProductPropertiesSection } from "../../item-properties/ItemProductPropertiesSection.js";
import { LabeledProductPropertiesValue } from "../../item-properties/LabeledProductPropertiesValue.js";
import { ProductCataloguePageButton } from "../../products/buttons/ProductCataloguePageButton.js";
import {
  productFiltersDefaultValue,
  ProductFiltersFormValues,
  productFiltersValidationSchema,
} from "../../products/filters/ProductFilterFields.js";
import { definitionsToFormValues, priceBound } from "../../products/helper.js";
import { ProductCreateModal } from "../../products/modals/ProductCreateModal.js";
import { ProductFilterModal } from "../../products/modals/ProductFilterModal.js";
import { ProductPreferencesSearchModal } from "../../products/modals/ProductPreferencesSearchModal.js";
import { ProductSearchModal } from "../../products/modals/ProductSearchModal.js";
import { ProductTypesSearchModal_PimProductTypeFragment } from "../../products/modals/ProductTypesSearchModal.generated.js";
import { ProductTypesSearchModal } from "../../products/modals/ProductTypesSearchModal.js";
import {
  PimProductPropertyFragment,
  PimProductTypePropertyTypeFragment,
  PimPropertyTypeFragment,
  ProductSearchItem__ProductSearchResultFragment,
} from "../../products/Product.generated.js";
import { ProductIcon } from "../../products/ProductIcon.js";
import { ProductPricing } from "../../products/ProductPricing.js";
import {
  BoolFilterOperatorOptions,
  NumberFilterOperatorOptions,
  PriceFilterOperatorOptions,
  TextFilterOperatorOptions,
} from "../../products/ProductSearchFilterOperatorOptions.js";
import {
  ItemProductSearchFilterBrandFilterFragment,
  ItemProductSearchFilterBrandLineFilterFragment,
  ItemProductSearchFilterDefinitionsFragment,
  ItemProductSearchFilterPriceFilterFragment,
} from "../../products/productSearchFilters.generated.js";
import { ItemProductSearchFilterPropertyFilterFragment } from "../../templates/templateSearchFilters.generated.js";
import { ProductTypeIcon } from "../../products/ProductTypeIcon.js";
import { ProductTypeInput } from "../../products/ProductTypeInput.js";
import {
  SupplierProductAlternativePicker__PimSupplierProductAlternativesProductFragment,
  SupplierProductAlternativePicker__PimSupplierProductAlternativesPropertyAlternativeFragment,
} from "../../products/SupplierProductAlternativePicker.generated.js";
import {
  OnProductSelectFn,
  SupplierProductPropertyMatchingProductAlternativesSelect,
} from "../../products/SupplierProductAlternativePicker.js";
import {
  buildFisSolrUrl,
  useFisSolr,
  useHasSapS4HanaIntegration,
  useItemProductIsConfigurable,
} from "../../sap-s4-hana/hooks.js";
import { FisSolrSearchModal } from "../../sap-s4-hana/richter-frenzel/FisSolrSearchModal.js";
import { S4HanaKmatProductConfigModal } from "../../sap-s4-hana/S4HanaKmatProductConfigureModal.js";
import { SupplierSelect } from "../../suppliers/SupplierSelect.js";
import { AiRephraseMenuItem } from "../menus/AiRephraseMenuItem.js";
import { isNotOwnProductPropertyOption } from "../modals/ExpressionInputWithVariables.js";
import { SearchFilterExpressionModal } from "../modals/SearchFilterExpressionModal.js";
import {
  getEnhancedPimPropertyTypeLabel,
  propToDefineInput,
} from "../properties.js";
import {
  QuestionControlSection,
  QuestionControlSectionFormValues,
} from "../QuestionControlSection.js";
import {
  useDefineItemProductSearchFilterDefinitionsMutation,
  useProductSearchFilterMutations,
} from "../useProductSearchFilterDefinitionMutations.js";
import { SearchFilterField } from "../SearchFilterField.js";
import {
  SearchFilterOperatorsModal,
  type SearchFilterOperatorRow,
} from "../modals/SearchFilterOperatorsModal.js";
import {
  getValueWithOperator,
  isComputedSearchFilterSlot,
  searchFilterEntry,
  searchFilterFieldPropsForSlot,
  searchFilterValueText,
  searchFilterOperator,
  searchFilterOperatorOptions,
  searchFilterSlotWithExpression,
  searchFilterSlotWithOperator,
  searchFilterSlotWithoutExpression,
} from "../searchFilterSlots.js";
import {
  EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE,
  useCanWriteExpressions,
} from "../expressionWriteAccess.js";
import {
  mergeProductFilterFormInput,
  barePriceFilterWithoutExpression,
  bareTextFilterWithoutExpression,
  newAuthoredProductFilterForPropertyType,
  productFilterFacetScope,
  productTypeToSetBeforeFilterSave,
  searchFilterValueOptionsFromPimPropertyType,
} from "../productSearchFilterSlots.js";
import { SearchFilterValueInput } from "../SearchFilterValueInput.js";
import {
  NamedBoolFilterInput,
  NamedMultiSelectFilterInput,
  NamedRangeFilterInput,
} from "../NamedSearchFilterValueInput.js";
import {
  useProductSearchFiltersSection_AvailableFiltersQuery,
  ProductBox_ItemFragment,
  ProductBox_ProjectFragment,
  ProductSearchFiltersSection_ItemFragment,
  ProductSelectQuestionControlForm_ItemFragment,
  useProductBox_ModifyItemProductMutation,
  useProductBox_ProductQuery,
  useProductBox_RemoveItemProductMutation,
  useProductBox_SetItemProductMutation,
  useProductBox_SupplierProductAlternativesQuery,
  useProductBox_SuppliersQuery,
  useProductSearchFiltersSection_ProductTypeLazyQuery,
} from "./ProductBox.generated.js";
import {
  useProductBox_RemoveItemProductPlaceholderMutationWithWizardRefetch,
  useProductBox_SetItemProductPlaceholderMutationWithWizardRefetch,
  useProductSelectQuestionControlForm_ModifyWizardSettingsMutationWithWizardRefetch,
} from "./ProductBox.mutations.js";
import { useAvailabilityWarning } from "../../quotes/alerts/QuoteProductsAvailabilityAlert.js";

interface FormValues {
  productArticleNumber?: string | null;
  productSupplier?: { id: string; title: string } | null;
  productTitle?: string;
  productDescription?: string;
  productBrand?: string;
  productBrandLine?: string;
  productMainBrandLine?: string;
  productGtin?: string | null;
  productManufacturerArticleNumber?: string | null;
  productCategory?: string;
  productTypeId?: string | null;
  productTypeTitle?: string | null;
  productTitleClientVisibility: Visibility;
  productDescriptionClientVisibility: Visibility;
  productBrandClientVisibility: Visibility;
  productProps2: Props2NonComputed[];
}

interface Props {
  project: ProductBox_ProjectFragment | null;
  docId: string;
  docType: DocType;
  documentViewerPermissions: PermissionName[];
  item: ProductBox_ItemFragment;
  isEditable?: boolean;
  viewerIsContractor?: boolean;
  isInitiallyClosed?: boolean;
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
  refetchWizardQuestions: () => Promise<unknown>;
}

export const ProductBox = ({
  project,
  docId,
  docType,
  documentViewerPermissions,
  item,
  isEditable = true,
  viewerIsContractor = false,
  isInitiallyClosed,
  onUpdateDataRefetchQueries,
  refetchWizardQuestions,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate([
    "QuoteItem",
    "Global",
    "Product",
    "ItemPropertyField",
    "SapS4Hana",
    "SupplierProductStatus",
  ]);

  const doProductAvailabilityWarning = useAvailabilityWarning();

  const { expandedItemIds } = useOutletContext<{
    expandedItemIds: string[] | undefined;
  }>();

  const [viewMode, setViewMode] = React.useState<ViewMode>(null);
  const [isFisSolrModalOpen, setIsFisSolrModalOpen] = React.useState(false);
  const { sapIntegrationSettings, showFisSolr } = useFisSolr();

  const client = useApolloClient();

  const productArticleNumberIdentifier = React.useMemo(() => {
    if (!item) return null;

    const productArticleNumber = !isUndefined(
      item.pendingChangeAttributes["productArticleNumber"]
    )
      ? item.pendingChangeAttributes["productArticleNumber"]
      : item.product
        ? item.product.articleNumber
        : null;

    const productSupplierId = !isUndefined(
      item.pendingChangeAttributes["productSupplierId"]
    )
      ? item.pendingChangeAttributes["productSupplierId"]
      : item.product
        ? item.product.supplierId
        : null;

    if (productArticleNumber && productSupplierId) {
      return {
        articleNumber: productArticleNumber,
        supplierId: productSupplierId,
      };
    } else {
      return null;
    }
  }, [item]);

  const selectedArticleNumber = productArticleNumberIdentifier?.articleNumber;
  const selectedSupplierId = productArticleNumberIdentifier?.supplierId;
  const placeholderProductKey =
    selectedArticleNumber && selectedSupplierId
      ? JSON.stringify([selectedArticleNumber, selectedSupplierId])
      : null;

  const productBoxProductQuery = useProductBox_ProductQuery({
    client,
    variables: {
      articleNumber: productArticleNumberIdentifier?.articleNumber,
      supplierId: productArticleNumberIdentifier?.supplierId,
    },
    skip: !(productArticleNumberIdentifier && viewerIsContractor),
    fetchPolicy: "network-only", //network-only here, because else we get problems with the product field!
  });

  const supplierProductAlternativesQuery =
    useProductBox_SupplierProductAlternativesQuery({
      client,
      variables: {
        articleNumber: productArticleNumberIdentifier?.articleNumber,
        supplierId: productArticleNumberIdentifier?.supplierId,
      },
      skip: !(productArticleNumberIdentifier && viewerIsContractor),
      fetchPolicy: "cache-and-network",
    });

  const productAlternatives = getDataOrNull(
    supplierProductAlternativesQuery.data?.pimSupplierProductAlternatives
  )?.result;

  const [setItemProduct, { loading: setItemProductLoading }] =
    useProductBox_SetItemProductMutation({
      client,
      refetchQueries: onUpdateDataRefetchQueries,
    });

  const [removeItemProduct, { loading: removeItemProductLoading }] =
    useProductBox_RemoveItemProductMutation({
      client,
      refetchQueries: onUpdateDataRefetchQueries,
    });
  const removeProduct = React.useCallback(async () => {
    await removeItemProduct({
      variables: {
        input: {
          projectId: project?.id,
          docId,
          itemId: item.id,
          expandedItemIds,
        },
      },
    });
  }, [removeItemProduct, project?.id, docId, item.id, expandedItemIds]);

  const [modifyItemProduct, { loading: modifyItemProductLoading }] =
    useProductBox_ModifyItemProductMutation({
      client,
      refetchQueries: onUpdateDataRefetchQueries,
    });
  const modifyProduct = React.useCallback(
    async (product: ModifyItemProductValuesInput) => {
      try {
        await modifyItemProduct({
          variables: {
            input: {
              projectId: project?.id,
              docId,
              itemId: item.id,
              values: product,
            },
          },
        });
      } catch (error) {
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
      }
    },
    [docId, enqueueSnackbar, project?.id, item.id, modifyItemProduct]
  );

  const itemAgreed = item.agreement === "YES";

  const initialValues: FormValues = {
    productArticleNumber: !isUndefined(
      item.pendingChangeAttributes["productArticleNumber"]
    )
      ? item.pendingChangeAttributes["productArticleNumber"]
      : item.product?.articleNumber,
    productSupplier: isUndefined(
      item.pendingChangeAttributes["productSupplierId"]
    )
      ? item.product?.supplier
      : isNull(item.pendingChangeAttributes["productSupplierId"])
        ? null
        : { id: item.pendingChangeAttributes["productSupplierId"], title: "" },
    productTitle: !isUndefined(item.pendingChangeAttributes["productTitle"])
      ? item.pendingChangeAttributes["productTitle"]
      : item.product?.title,
    productDescription: !isUndefined(
      item.pendingChangeAttributes["productDescription"]
    )
      ? item.pendingChangeAttributes["productDescription"]
      : item.product?.description,
    productBrand: !isUndefined(item.pendingChangeAttributes["productBrand"])
      ? item.pendingChangeAttributes["productBrand"]
      : item.product?.brand,
    productBrandLine: !isUndefined(
      item.pendingChangeAttributes["productBrandLine"]
    )
      ? item.pendingChangeAttributes["productBrandLine"]
      : item.product?.brandLine,
    productMainBrandLine: !isUndefined(
      item.pendingChangeAttributes["productMainBrandLine"]
    )
      ? item.pendingChangeAttributes["productMainBrandLine"]
      : item.product?.mainBrandLine,
    productGtin: !isUndefined(item.pendingChangeAttributes["productGtin"])
      ? item.pendingChangeAttributes["productGtin"]
      : item.product?.gtin,
    productManufacturerArticleNumber: !isUndefined(
      item.pendingChangeAttributes["productManufacturerArticleNumber"]
    )
      ? item.pendingChangeAttributes["productManufacturerArticleNumber"]
      : item.product?.manufacturerArticleNumber,
    productCategory: !isUndefined(
      item.pendingChangeAttributes["productCategory"]
    )
      ? item.pendingChangeAttributes["productCategory"]
      : item.product?.productCategory,
    productTypeId: !isUndefined(item.pendingChangeAttributes["productTypeId"])
      ? item.pendingChangeAttributes["productTypeId"]
      : item.product?.productTypeId,
    productTypeTitle: !isUndefined(
      item.pendingChangeAttributes["productTypeTitle"]
    )
      ? item.pendingChangeAttributes["productTypeTitle"]
      : item.product?.productTypeTitle,
    productTitleClientVisibility: !isUndefined(
      item.pendingChangeAttributes["productTitleClientVisibility"]
    )
      ? item.pendingChangeAttributes["productTitleClientVisibility"]
      : item.product?.titleClientVisibility,
    productDescriptionClientVisibility: !isUndefined(
      item.pendingChangeAttributes["productDescriptionClientVisibility"]
    )
      ? item.pendingChangeAttributes["productDescriptionClientVisibility"]
      : item.product?.descriptionClientVisibility,
    productBrandClientVisibility: !isUndefined(
      item.pendingChangeAttributes["productBrandClientVisibility"]
    )
      ? item.pendingChangeAttributes["productBrandClientVisibility"]
      : item.product?.brandClientVisibility,
    productProps2:
      (!isUndefined(item.pendingChangeAttributes["productProps2"])
        ? item.pendingChangeAttributes["productProps2"]
        : item.product?.props2) ?? [],
  };

  const hasProduct =
    (initialValues.productTitle && initialValues.productTitle.length > 0) ||
    initialValues.productArticleNumber;

  const pimCustomSupplierProduct = getDataOrNull(
    productBoxProductQuery?.data?.pimGetProduct
  )?.product;

  // A manually entered product can carry an article number and supplier too;
  // only a settled lookup that finds no catalogue record tells it apart.
  const catalogueRecordAbsent =
    !productBoxProductQuery.loading &&
    productBoxProductQuery.data?.pimGetProduct.__typename ===
      "PimGetProductResult" &&
    !pimCustomSupplierProduct;
  const hasPimSelection =
    !!productArticleNumberIdentifier &&
    viewerIsContractor &&
    !catalogueRecordAbsent;

  const isLoading =
    setItemProductLoading ||
    modifyItemProductLoading ||
    removeItemProductLoading;

  const productSearchFilters: ProductFiltersFormValues =
    definitionsToFormValues(item.productSearchFilterDefaults, item);

  const [defineItemProductSearchFilterDefinitions] =
    useDefineItemProductSearchFilterDefinitionsMutation([
      namedOperations.Query.ProductBox_Product,
    ]);
  const [setItemProductPlaceholder] =
    useProductBox_SetItemProductPlaceholderMutationWithWizardRefetch({
      client,
      refetchQueries: [namedOperations.Query.ProductBox_Product],
      refetchWizardQuestions,
    });
  const setProductType = (productTypeId: string) =>
    setItemProductPlaceholder({
      variables: {
        input: {
          projectId: project?.id,
          docId,
          itemId: item.id,
          productTypeId,
        },
      },
    });

  const onFiltersSave = async (values: ProductFiltersFormValues) => {
    const productTypeToSet = productTypeToSetBeforeFilterSave(
      values.productTypeIds,
      item.productPlaceholder
    );
    if (productTypeToSet) {
      await setProductType(productTypeToSet);
    }
    await defineItemProductSearchFilterDefinitions({
      variables: {
        input: {
          projectId: project?.id,
          docId,
          itemId: item.id,
          // The stored filters belong to the old type once it changed, so
          // only the form's own filters are written then.
          ...(productTypeToSet
            ? filterFormValuesToDefinitionInput(values)
            : mergeProductFilterFormInput(
                filterFormValuesToDefinitionInput(values),
                item.productSearchFilterDefinitions
              )),
        },
      },
    });
  };

  const onProductSelect = React.useCallback(
    async (
      product: Pick<
        ProductSearchItem__ProductSearchResultFragment,
        "articleNumber" | "supplierId" | "status"
      >
    ) => {
      try {
        await setItemProduct({
          variables: {
            input: {
              projectId: project?.id,
              docId,
              itemId: item.id,
              productArticleNumber: product.articleNumber,
              productSupplierId: product.supplierId,
              expandedItemIds,
            },
          },
        });

        if (product.status && product.status.value !== "available") {
          const status = product.status.value;

          doProductAvailabilityWarning(status);
        }
      } catch (error) {
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
      }
      setViewMode(null);
    },
    [
      setItemProduct,
      docId,
      item.id,
      project?.id,
      enqueueSnackbar,
      expandedItemIds,
      doProductAvailabilityWarning,
    ]
  );

  const onProductCreate = React.useCallback(
    async (values: FormValues) => {
      await removeProduct();
      await modifyProduct(productFormValuesToInput(values));
      setViewMode(null);
    },
    [modifyProduct, removeProduct]
  );

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const { isConfigurable: productIsConfigurable, refetchQueries } =
    useItemProductIsConfigurable({
      itemProductArticleNumber: initialValues.productArticleNumber,
      product: pimCustomSupplierProduct,
      projectId: project?.id,
      quoteId: docId,
      itemId: item.id,
    });

  const placeholderSection =
    viewerIsContractor && item.productPlaceholder ? (
      <ProductSearchFiltersSection
        projectId={project?.id ?? null}
        docId={docId}
        docType={docType}
        item={item}
        productTypeId={item.productPlaceholder.productTypeId}
        isEditable={isEditable}
        documentViewerPermissions={documentViewerPermissions}
        refetchWizardQuestions={refetchWizardQuestions}
      />
    ) : null;

  return (
    <>
      <CardContainer
        isExpandable={true}
        title={t("Product", { ns: "QuoteItem" })}
        isInitiallyClosed={isInitiallyClosed}
        isLoading={isLoading}
        ActionButton={
          <Stack direction={"row"} spacing={0.5}>
            {hasSapS4HanaIntegration &&
              initialValues.productArticleNumber &&
              productIsConfigurable &&
              project && (
                <ModalOpenButton
                  Modal={S4HanaKmatProductConfigModal}
                  modalProps={{
                    projectId: project.id,
                    quoteId: docId,
                    itemId: item.id,
                    productId: initialValues.productArticleNumber,
                    manufacturerArticleNumber:
                      initialValues?.productManufacturerArticleNumber,
                    netPrice:
                      pimCustomSupplierProduct?.pricing?.netPrice ??
                      item.materialBuyingPrice,
                    refetchQueries: (onUpdateDataRefetchQueries ?? []).concat(
                      refetchQueries
                    ),
                  }}
                >
                  <Tooltip title={t("Configure product", { ns: "SapS4Hana" })}>
                    <IconButton size="small" color="warning">
                      <Icon>
                        <SapLogo />
                      </Icon>
                    </IconButton>
                  </Tooltip>
                </ModalOpenButton>
              )}
            {/* Not gated on document editability: preferring an article writes to the
                catalogue, not to this template, so a reader of the template who may edit the
                catalogue can still set preferences for its placeholders. */}
            {docType === "TEMPLATE" &&
              !pimCustomSupplierProduct &&
              item.productPlaceholder && (
                <RestrictedByCapabilityWithDebug capability="pim">
                  <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
                    <ModalOpenButton
                      Modal={ProductPreferencesSearchModal}
                      modalProps={{
                        productTypeId: item.productPlaceholder.productTypeId,
                      }}
                    >
                      <Tooltip
                        title={t(
                          "Mark the products you prefer for this product type. They are ranked first when this placeholder is filled.",
                          { ns: "Product" }
                        )}
                      >
                        <IconButton size="small" color="primary">
                          <BookmarkIcon />
                        </IconButton>
                      </Tooltip>
                    </ModalOpenButton>
                  </RestrictedByOrganisationPermissionWithDebug>
                </RestrictedByCapabilityWithDebug>
              )}
            {!isEditable ||
            viewMode === "edit" ||
            !(hasProduct || ALLOW_AI_FEATURE) ? null : (
              <MenuButton dataTestId="product-box-menu">
                {hasProduct &&
                  !productIsConfigurable && [
                    <ViewModeMenuItem
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      allowedModes={[null, "info", "edit"]}
                      key="view-mode-item"
                    />,
                    <Divider key="view-mode-divider" />,
                  ]}
                {ALLOW_AI_FEATURE && [
                  <AiRephraseMenuItem
                    itemId={item.id}
                    docId={docId}
                    projectId={project?.id ?? null}
                    key="ai-rephrase-item"
                  />,
                  <Divider key="ai-rephrase-divider" />,
                ]}
                {hasProduct && showFisSolr && (
                  <MenuItemWithIcon
                    icon={<SearchIcon />}
                    onClick={() => {
                      const url = buildFisSolrUrl(sapIntegrationSettings, {
                        itemId: item.id,
                        quoteId: docId,
                        mode: "REPLACE",
                      });
                      if (url) {
                        window.open(url, "fis-solr-tab");
                        setIsFisSolrModalOpen(true);
                      }
                    }}
                  >
                    {t("Replace product with FIS SOLR", { ns: "SapS4Hana" })}
                  </MenuItemWithIcon>
                )}
                {hasProduct && !showFisSolr && (
                  <RestrictedByCapabilityWithDebug
                    capability="pim"
                    otherwise={
                      <ModalOpenButton
                        Modal={ProductCreateModal}
                        modalProps={{
                          handleSubmit: onProductCreate,
                        }}
                      >
                        <MenuItemWithIcon icon={<SearchIcon />}>
                          {t("Replace product", { ns: "QuoteItem" })}
                        </MenuItemWithIcon>
                      </ModalOpenButton>
                    }
                  >
                    <ModalOpenButton
                      Modal={ProductSearchModal}
                      modalProps={{
                        handleProductChoice: onProductSelect,
                        handleProductCreate: onProductCreate,
                        handleFiltersSave: onFiltersSave,
                        initialFilters: productSearchFilters,
                        canChangeProductType: !item.productPlaceholder,
                        includePreferences: true,
                      }}
                    >
                      <MenuItemWithIcon
                        icon={<SearchIcon />}
                        data-testid="product-box-replace-product"
                      >
                        {t("Replace product", { ns: "QuoteItem" })}
                      </MenuItemWithIcon>
                    </ModalOpenButton>
                  </RestrictedByCapabilityWithDebug>
                )}
                {hasProduct && (
                  <ModalOpenButton
                    Modal={ConfirmModal}
                    modalProps={{
                      title: t("Confirm deletion", { ns: "Global" }),
                      text: t(
                        "Deletion cannot be undone. Do you want to continue?",
                        {
                          ns: "Global",
                        }
                      ),
                      handleConfirm: async () => {
                        await removeProduct();
                        setViewMode(null);
                      },
                    }}
                  >
                    <MenuItemWithIcon icon={<DeleteIcon />}>
                      {t("Remove product", { ns: "QuoteItem" })}
                    </MenuItemWithIcon>
                  </ModalOpenButton>
                )}
              </MenuButton>
            )}
            {isEditable && !hasProduct && (
              <MenuButton Icon={<AddIcon />} dataTestId="product-box-add-menu">
                {!hasProduct && (
                  <RestrictedByCapabilityWithDebug
                    capability="pim"
                    otherwise={
                      <ModalOpenButton
                        Modal={ProductCreateModal}
                        modalProps={{
                          handleSubmit: onProductCreate,
                        }}
                      >
                        <MenuItemWithIcon icon={<ProductIcon />}>
                          {t("Add product", { ns: "QuoteItem" })}
                        </MenuItemWithIcon>
                      </ModalOpenButton>
                    }
                  >
                    <ModalOpenButton
                      Modal={ProductSearchModal}
                      modalProps={{
                        handleProductChoice: onProductSelect,
                        handleProductCreate: onProductCreate,
                        handleFiltersSave: onFiltersSave,
                        initialFilters: productSearchFilters,
                        canChangeProductType: !item.productPlaceholder,
                        includePreferences: true,
                      }}
                    >
                      <MenuItemWithIcon
                        icon={<ProductIcon />}
                        data-testid="product-box-add-product"
                      >
                        {t("Add product", { ns: "QuoteItem" })}
                      </MenuItemWithIcon>
                    </ModalOpenButton>
                  </RestrictedByCapabilityWithDebug>
                )}
                {!item.productPlaceholder && (
                  <RestrictedByCapabilityWithDebug capability="pim">
                    <ModalOpenButton
                      Modal={ProductTypesSearchModal}
                      modalProps={{
                        productTypeSystem: "REFERENCE",
                        handleSelectProductType: async productType => {
                          await setProductType(productType.id);
                        },
                      }}
                    >
                      <MenuItemWithIcon icon={<ProductTypeIcon />}>
                        {t("Add product type", { ns: "QuoteItem" })}
                      </MenuItemWithIcon>
                    </ModalOpenButton>
                  </RestrictedByCapabilityWithDebug>
                )}
              </MenuButton>
            )}
          </Stack>
        }
      >
        <Box
          sx={{
            padding: 1,
          }}
        >
          {item.isUnresolvedProductPlaceholder &&
            !hasProduct &&
            initialValues.productProps2.length > 0 && (
              <ProductPropertiesCollapseSection
                properties={initialValues.productProps2}
                viewerIsContractor={viewerIsContractor}
                hideVisibility
              />
            )}
          {!hasProduct ? (
            isEditable ? (
              !item.productPlaceholder && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RestrictedByCapabilityWithDebug
                    capability="pim"
                    otherwise={
                      <ModalOpenButton
                        Modal={ProductCreateModal}
                        modalProps={{
                          handleSubmit: onProductCreate,
                        }}
                      >
                        <Button
                          startIcon={<AddIcon />}
                          color={"secondary"}
                          size="small"
                        >
                          {t("Add product", {
                            ns: "QuoteItem",
                          })}
                        </Button>
                      </ModalOpenButton>
                    }
                  >
                    <ModalOpenButton
                      Modal={ProductSearchModal}
                      modalProps={{
                        handleProductChoice: onProductSelect,
                        handleProductCreate: onProductCreate,
                        handleFiltersSave: onFiltersSave,
                        initialFilters: productSearchFilters,
                        canChangeProductType: !item.productPlaceholder,
                        includePreferences: true,
                      }}
                    >
                      <Button
                        startIcon={<AddIcon />}
                        color={"secondary"}
                        size="small"
                      >
                        {t("Add product", {
                          ns: "QuoteItem",
                        })}
                      </Button>
                    </ModalOpenButton>
                  </RestrictedByCapabilityWithDebug>
                  {!item.productPlaceholder && (
                    <RestrictedByCapabilityWithDebug capability="pim">
                      <Typography>{t("or", { ns: "Global" })}</Typography>
                      <ModalOpenButton
                        Modal={ProductTypesSearchModal}
                        modalProps={{
                          productTypeSystem: "REFERENCE",
                          handleSelectProductType: async productType => {
                            await setProductType(productType.id);
                          },
                        }}
                      >
                        <Button
                          startIcon={<AddIcon />}
                          color={"secondary"}
                          size="small"
                        >
                          {t("Add product type", {
                            ns: "QuoteItem",
                          })}
                        </Button>
                      </ModalOpenButton>
                    </RestrictedByCapabilityWithDebug>
                  )}
                </Stack>
              )
            ) : (
              !item.productPlaceholder && (
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "grey.600",
                    }}
                  >
                    {t("No product added", {
                      ns: "QuoteItem",
                    })}
                  </Typography>
                </Stack>
              )
            )
          ) : !viewerIsContractor ||
            (viewerIsContractor &&
              (viewMode === null || viewMode === "info")) ? (
            <ProductReadView
              item={initialValues}
              product={pimCustomSupplierProduct}
              viewerIsContractor={viewerIsContractor}
              isEditable={isEditable}
              viewMode={viewMode}
              onProductSelect={onProductSelect}
              productAlternatives={productAlternatives ?? null}
              visibilityEditable={itemAgreed && isEditable}
              setVisibility={(fieldName, value) =>
                modifyItemProduct({
                  variables: {
                    input: {
                      projectId: project?.id,
                      docId,
                      itemId: item.id,
                      values: {
                        [fieldName]: value,
                      },
                    },
                  },
                })
              }
            />
          ) : isEditable && viewMode === "edit" ? (
            <ProductEditView
              docType={docType}
              item={initialValues}
              product={pimCustomSupplierProduct}
              itemMaterialBuyingPrice={item.materialBuyingPrice}
              onSubmit={async values => {
                await modifyProduct(productFormValuesToInput(values));
                setViewMode(null);
              }}
              isSibmitting={modifyItemProductLoading}
              onCancel={() => setViewMode(null)}
            />
          ) : null}

          {viewerIsContractor && pimCustomSupplierProduct && (
            <RestrictedByCapabilityWithDebug capability="pim">
              <RestrictedByDocumentPermissionByTypeWithDebug
                permissionByType={{
                  QUOTE: "quote_document_update",
                  TEMPLATE: "template_document_update",
                }}
                document={{
                  docType,
                  viewerPermissions: documentViewerPermissions,
                }}
              >
                <ProductPricing product={pimCustomSupplierProduct} />
              </RestrictedByDocumentPermissionByTypeWithDebug>
            </RestrictedByCapabilityWithDebug>
          )}
          {hasPimSelection && placeholderSection && (
            <>
              <Divider sx={{ marginX: -1, paddingTop: 1 }} />
              <PlaceholderCollapseSection key={placeholderProductKey}>
                {placeholderSection}
              </PlaceholderCollapseSection>
            </>
          )}
          {viewerIsContractor && pimCustomSupplierProduct && (
            <RestrictedByCapabilityWithDebug capability="pim">
              <RestrictedByDocumentPermissionByTypeWithDebug
                permissionByType={{
                  QUOTE: "quote_document_update",
                  TEMPLATE: "template_document_update",
                }}
                document={{
                  docType,
                  viewerPermissions: documentViewerPermissions,
                }}
              >
                <Divider sx={{ marginX: -1, paddingTop: 1 }} />
                <Stack
                  sx={{
                    alignContent: "flex-start",
                    pt: 1,
                  }}
                >
                  <ProductCataloguePageButton
                    productArticleNumber={
                      pimCustomSupplierProduct.articleNumber
                    }
                    productSupplierId={pimCustomSupplierProduct.supplierId}
                  />
                </Stack>
              </RestrictedByDocumentPermissionByTypeWithDebug>
            </RestrictedByCapabilityWithDebug>
          )}
          {!hasPimSelection && placeholderSection}
        </Box>
      </CardContainer>
      {isFisSolrModalOpen && (
        <FisSolrSearchModal handleClose={() => setIsFisSolrModalOpen(false)} />
      )}
    </>
  );
};

function filterFormValuesToDefinitionInput(
  values: ProductFiltersFormValues
): Omit<
  DefineItemProductSearchFilterDefinitionsInput,
  "projectId" | "docId" | "itemId"
> {
  return {
    favouritesFilter:
      values.organisationFavourite === true
        ? { boolFilter: { valueBool: true, operatorBool: "eq" } }
        : undefined,
    supplierIdFilter: values.supplierIds
      ? {
          idInFilter: {
            operatorIdIn: "in",
            valueIdIn: values.supplierIds,
          },
        }
      : undefined,
    brandFilter: values.brands
      ? {
          textInFilter: {
            operatorTextIn: "in",
            valueTextIn: values.brands,
          },
        }
      : undefined,
    brandLineFilter: values.brandLines
      ? {
          textInFilter: {
            operatorTextIn: "in",
            valueTextIn: values.brandLines,
          },
        }
      : undefined,
    // A single bound is a valid one-sided clause, which is what the filter row
    // writes when only one side is filled — and `0` is a bound like any other.
    listPriceFilter:
      values.listPriceMin !== null && values.listPriceMax !== null
        ? {
            numberBetweenFilter: {
              operatorNumberBetween: "between",
              valueNumberBetween: {
                min: values.listPriceMin,
                max: values.listPriceMax,
              },
            },
          }
        : values.listPriceMin !== null || values.listPriceMax !== null
          ? {
              numberFilter: {
                operatorNumber: values.listPriceMax !== null ? "lte" : "gte",
                valueNumber: values.listPriceMax ?? values.listPriceMin,
              },
            }
          : undefined,
    propertyFilters: (values.properties ?? []).flatMap(filter => {
      if (filter.boolFilter) {
        return {
          boolFilter: {
            key: filter.key,
            operatorBool: filter.boolFilter.operatorBool,
            valueBool: filter.boolFilter.valueBool,
          },
        };
      }
      if (filter.boolInFilter) {
        return {
          boolInFilter: {
            key: filter.key,
            operatorBoolIn: filter.boolInFilter.operatorBoolIn,
            valueBoolIn: filter.boolInFilter.valueBoolIn,
          },
        };
      }
      if (filter.numberBetweenFilter) {
        return {
          numberBetweenFilter: {
            key: filter.key,
            operatorNumberBetween:
              filter.numberBetweenFilter.operatorNumberBetween,
            valueNumberBetween: filter.numberBetweenFilter.valueNumberBetween,
          },
        };
      }
      if (filter.numberFilter) {
        return {
          numberFilter: {
            key: filter.key,
            operatorNumber: filter.numberFilter.operatorNumber,
            valueNumber: filter.numberFilter.valueNumber,
          },
        };
      }
      if (filter.numberInFilter) {
        return {
          numberInFilter: {
            key: filter.key,
            operatorNumberIn: filter.numberInFilter.operatorNumberIn,
            valueNumberIn: filter.numberInFilter.valueNumberIn,
          },
        };
      }
      if (filter.textFilter) {
        return {
          textFilter: {
            key: filter.key,
            operatorText: filter.textFilter.operatorText,
            valueText: filter.textFilter.valueText,
          },
        };
      }
      if (filter.textInFilter) {
        return {
          textInFilter: {
            key: filter.key,
            operatorTextIn: filter.textInFilter.operatorTextIn,
            valueTextIn: filter.textInFilter.valueTextIn,
          },
        };
      }

      throw new Error("Unknown filter type");
    }),
  };
}

export const productFormValuesToInput = (
  values: FormValues
): ModifyItemProductValuesInput => {
  return {
    productArticleNumber: values.productArticleNumber ?? null,
    productSupplierId: values.productSupplier?.id ?? null,
    productTitle: values.productTitle,
    productDescription: values.productDescription,
    productBrand: values.productBrand,
    productBrandLine: values.productBrandLine,
    productMainBrandLine: values.productMainBrandLine,
    productGtin: values.productGtin ?? null,
    productManufacturerArticleNumber:
      values.productManufacturerArticleNumber ?? null,
    productCategory: values.productCategory,
    productTypeId: values.productTypeId,
    productTypeTitle: values.productTypeTitle,
    productTitleClientVisibility: values.productTitleClientVisibility,
    productDescriptionClientVisibility:
      values.productDescriptionClientVisibility,
    productBrandClientVisibility: values.productBrandClientVisibility,
    productProps2: values.productProps2.map(propToDefineInput),
  };
};

interface ProductEditViewProps {
  docType: DocType;
  item: FormValues;
  product?: {
    id: string;
    articleNumber: string;
    supplierId: string;
    pricing?:
      | { netPrice?: number | null; listPrice?: number | null }
      | null
      | undefined;
  } | null;
  itemMaterialBuyingPrice?: number;
  onSubmit: FormikConfig<FormValues>["onSubmit"];
  isSibmitting: boolean;
  onCancel: () => void;
}

const ProductEditView = ({
  docType,
  item,
  product,
  onSubmit,
  isSibmitting,
  onCancel,
}: ProductEditViewProps) => {
  const { t } = useTranslate([
    "QuoteItem",
    "Global",
    "Product",
    "ItemPropertyField",
  ]);

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        productArticleNumber: Yup.string()
          .nullable()
          .label(
            t("Article number", {
              ns: "Product",
            })
          ),
        productSupplier: Yup.object()
          .shape({ id: Yup.string(), title: Yup.string() })
          .nullable()
          .label(t("Supplier", { ns: "Product" })),
        productTitle: Yup.string()
          .trim()
          .required(
            t("Product title required", {
              ns: "QuoteItem",
            })
          )
          .label(
            t("Title", {
              ns: "Product",
            })
          ),
        productDescription: Yup.string().label(
          t("Description", {
            ns: "Product",
          })
        ),
        productBrand: Yup.string().label(
          t("Brand", {
            ns: "Product",
          })
        ),
        productBrandLine: Yup.string().label(
          t("Brand line", {
            ns: "Product",
          })
        ),
        productMainBrandLine: Yup.string().label(
          t("Main brand line", {
            ns: "Product",
          })
        ),
        productGtin: Yup.string()
          .nullable()
          .label(
            t("GTIN", {
              ns: "Product",
            })
          ),
        productManufacturerArticleNumber: Yup.string()
          .nullable()
          .label(
            t("Manufacturer article number", {
              ns: "Product",
            })
          ),
        productCategory: Yup.string().label(
          t("Category", {
            ns: "Product",
          })
        ),
        productTypeId: Yup.string().nullable(),
        productTypeTitle: Yup.string().nullable(),
        productTitleClientVisibility: Yup.string().oneOf([
          "none",
          "show",
          "hide",
        ]),
        productDescriptionClientVisibility: Yup.string().oneOf([
          "none",
          "show",
          "hide",
        ]),
        productBrandClientVisibility: Yup.string().oneOf([
          "none",
          "show",
          "hide",
        ]),
        // TODO: product properties put correct validation
        productProps2: Yup.mixed(),
      }),
    [t]
  );

  const gridColumnSpacing = 1;

  return (
    <Formik<FormValues>
      initialValues={item}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {formikProps => (
        <Form>
          <Grid
            container
            columns={2}
            rowSpacing={1}
            columnSpacing={gridColumnSpacing}
          >
            {product && (
              <Grid size={2}>
                <Alert severity="info" variant="outlined">
                  {t(
                    "Editing product details on a quote item only changes what information you share with the client. To edit product details permanently, go to the catalogue page.",
                    { ns: "Product" }
                  )}
                </Alert>
              </Grid>
            )}
            <Grid size={2}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  label={t("Product title", {
                    ns: "QuoteItem",
                  })}
                  name="productTitle"
                  disabled={false}
                  required
                  // value={formikProps.values.productTitle}
                />
                <VisibilityButton
                  visibility={formikProps.values.productTitleClientVisibility}
                  onChange={visibility =>
                    formikProps.setFieldValue(
                      "productTitleClientVisibility",
                      visibility
                    )
                  }
                />
              </Stack>
            </Grid>
            <Grid size={2}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <RichTextField
                    name="productDescription"
                    label={t("Product description", {
                      ns: "QuoteItem",
                    })}
                    disabled={false}
                  />
                </Box>
                <VisibilityButton
                  visibility={
                    formikProps.values.productDescriptionClientVisibility
                  }
                  onChange={visibility =>
                    formikProps.setFieldValue(
                      "productDescriptionClientVisibility",
                      visibility
                    )
                  }
                />
              </Stack>
            </Grid>
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {!product ? (
                  <TextField
                    label={t("Category", {
                      ns: "Product",
                    })}
                    name="productCategory"
                  />
                ) : (
                  <LabeledValue
                    label={t("Category", {
                      ns: "Product",
                    })}
                  >
                    <ValueWithDiff
                      value={item.productCategory}
                      spacing={0}
                      showDiff={false}
                      placeholder={"-"}
                    />
                  </LabeledValue>
                )}
                <VisibilityIndicatorIcon isVisible={false} />
              </Stack>
            </Grid>
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {!product ? (
                  <TextField
                    label={t("Article number", {
                      ns: "Product",
                    })}
                    name="productArticleNumber"
                  />
                ) : (
                  <LabeledValue
                    label={t("Article number", {
                      ns: "Product",
                    })}
                  >
                    <ValueWithDiff
                      value={item.productArticleNumber}
                      showDiff={false}
                      spacing={0}
                      placeholder={"-"}
                    />
                  </LabeledValue>
                )}
                <VisibilityIndicatorIcon isVisible={false} />
              </Stack>
            </Grid>
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  label={t("GTIN", {
                    ns: "Product",
                  })}
                  name="productGtin"
                />
                <VisibilityIndicatorIcon isVisible={false} />
              </Stack>
            </Grid>
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  label={t("Manufacturer article number", {
                    ns: "Product",
                  })}
                  name="productManufacturerArticleNumber"
                />
                <VisibilityIndicatorIcon isVisible={false} />
              </Stack>
            </Grid>
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  label={t("Brand", {
                    ns: "Product",
                  })}
                  name="productBrand"
                />
                <VisibilityButton
                  visibility={formikProps.values.productBrandClientVisibility}
                  onChange={visibility =>
                    formikProps.setFieldValue(
                      "productBrandClientVisibility",
                      visibility
                    )
                  }
                />
              </Stack>
            </Grid>
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  label={t("Brand line", {
                    ns: "Product",
                  })}
                  name="productBrandLine"
                />
                <VisibilityButton
                  visibility={formikProps.values.productBrandClientVisibility}
                  onChange={noop}
                  disabled={true}
                />
              </Stack>
            </Grid>
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  label={t("Main brand line", {
                    ns: "Product",
                  })}
                  name="productMainBrandLine"
                />
                <VisibilityButton
                  visibility={formikProps.values.productBrandClientVisibility}
                  onChange={noop}
                  disabled={true}
                />
              </Stack>
            </Grid>
            <Grid size={2}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {!product ? (
                  <SupplierSelect
                    isSubscribed={true}
                    required={false}
                    disabled={formikProps.isSubmitting}
                    productSupplierId={
                      formikProps.values.productSupplier?.id ?? null
                    }
                    onChange={productSupplier => {
                      formikProps.setFieldValue(
                        "productSupplier",
                        productSupplier
                      );
                    }}
                  />
                ) : (
                  <LabeledValue
                    label={t("Supplier", {
                      ns: "Product",
                    })}
                  >
                    <ValueWithDiff
                      value={item.productSupplier?.title}
                      showDiff={false}
                      spacing={0}
                      placeholder={"-"}
                    />
                  </LabeledValue>
                )}
                <VisibilityIndicatorIcon isVisible={false} />
              </Stack>
            </Grid>
            <Grid size={2}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <TextField
                  label={t("Product Type", {
                    ns: "Product",
                  })}
                  name="productTypeTitle"
                />
                <VisibilityIndicatorIcon isVisible={false} />
              </Stack>
            </Grid>
            <Grid size={2}>
              <ItemProductPropertiesSection
                fieldName={"productProps2"}
                title={t("Product Properties", {
                  ns: "QuoteItem",
                })}
                properties={formikProps.values.productProps2}
                docType={docType}
              />
            </Grid>
            <Grid size={2}>
              <ManualSave
                disabled={isSibmitting}
                disabledSubmit={!formikProps.isValid || !formikProps.dirty}
                onCancel={onCancel}
              />
            </Grid>
            {/* <NavigationPrompt when={formikProps.dirty} /> */}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

interface ProductPropertiesCollapseSectionProps {
  properties: Props2NonComputed[];
  originalProperties?: Props2NonComputed[];
  viewerIsContractor: boolean;
  hideVisibility?: boolean;
  showInfo?: boolean;
  propertyAlternatives?: SupplierProductAlternativePicker__PimSupplierProductAlternativesPropertyAlternativeFragment[];
  onProductSelect?: OnProductSelectFn;
  gridColumnSpacing?: number;
}

/**
 * The item's product-properties section, for both an assigned product and the
 * property surface materialised from a product type. A property the client may
 * not see is dropped unless the viewer is the contractor.
 */
const ProductPropertiesCollapseSection = ({
  properties,
  originalProperties,
  viewerIsContractor,
  ...rest
}: ProductPropertiesCollapseSectionProps) => {
  const { t } = useTranslate("QuoteItem");

  const isVisible = (property: Props2NonComputed) =>
    property.clientVisibility || viewerIsContractor;

  const visibleProperties = properties.filter(isVisible);
  const visibleOriginalProperties = originalProperties?.filter(isVisible);

  return (
    <CollapseSection
      id="item-product-properties"
      title={t("Product Properties", { ns: "QuoteItem" })}
      titleVariant="h4"
      itemCount={visibleProperties.length}
      hideItemCountOnExpand={true}
      isInitiallyExpanded={
        visibleProperties.length > 0 ||
        (visibleOriginalProperties !== undefined &&
          visibleOriginalProperties.length > 0)
      }
    >
      <LabeledProductPropertiesValue
        properties={visibleProperties}
        originalProperties={visibleOriginalProperties}
        viewerIsContractor={viewerIsContractor}
        {...rest}
      />
    </CollapseSection>
  );
};

interface ProductReadViewProps {
  item: FormValues;
  product?: {
    id: string;
    articleNumber: string;
    gtin?: string | null;
    manufacturerArticleNumber?: string | null;
    supplier?: { id: string; title: string } | null;
    supplierId: string;
    category: string;
    texts?: {
      title?: string | null;
      clientTitleText?: string | null;
      clientDescriptionText?: string | null;
    } | null;
    pricing?: { netPrice?: number | null; listPrice?: number | null } | null;
    manufacturer?: { id: string; title: string } | null;
    brand?: string;
    branding?: {
      brand: { title: string };
      brandLine?: {
        title: string;
        mainBrandLine?: { title: string } | null;
      } | null;
    } | null;
    productVariantGroup?: {
      id: string;
      title: string;
    } | null;
    referenceTyping?: {
      productType?: { id: string; label: string } | null;
    } | null;
    properties?: PimProductPropertyFragment[];
    status?: { value?: PimSupplierProductStatusValue | null | null } | null;
  } | null;
  viewerIsContractor: boolean;
  visibilityEditable?: boolean;
  setVisibility: (fieldName: keyof FormValues, value: Visibility) => void;
  isEditable: boolean;
  productAlternatives: {
    propertyAlternatives: SupplierProductAlternativePicker__PimSupplierProductAlternativesPropertyAlternativeFragment[];
    propertyMatchingProductAlternatives: SupplierProductAlternativePicker__PimSupplierProductAlternativesProductFragment[];
  } | null;
  onProductSelect: OnProductSelectFn;
  viewMode: ViewMode;
}

export const ProductReadView = ({
  item,
  product,
  viewerIsContractor,
  visibilityEditable,
  setVisibility,
  isEditable,
  productAlternatives,
  onProductSelect,
  viewMode,
}: ProductReadViewProps) => {
  const { t } = useTranslate([
    "QuoteItem",
    "Global",
    "Product",
    "ItemPropertyField",
    "SupplierProductStatus",
  ]);

  const gridColumnSpacing = 1;

  const properties = item.productProps2;
  const originalProperties = product?.properties?.map(p => p.prop2);

  const statusAlert =
    product && product.status && product.status.value !== "available"
      ? product.status.value
      : null;

  return (
    <Stack direction="column" spacing={1}>
      {statusAlert && (
        <Alert severity="warning" variant="outlined">
          {t("Product availability to be checked on ordering", {
            ns: "Product",
            status: capitalize(t(statusAlert, { ns: "SupplierProductStatus" })),
          })}
        </Alert>
      )}
      <Box>
        <Grid
          container
          columns={2}
          rowSpacing={1}
          columnSpacing={gridColumnSpacing}
        >
          {(viewerIsContractor ||
            item.productTitleClientVisibility !== "hide") && (
            <Grid size={2}>
              <ValueWithDiff
                value={item.productTitle}
                ValueComponent={({ value }) => (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant={"h3"}>{value}</Typography>
                    {viewerIsContractor && (
                      <VisibilityButton
                        visibility={item.productTitleClientVisibility}
                        onChange={visibility =>
                          setVisibility(
                            "productTitleClientVisibility",
                            visibility
                          )
                        }
                        disabled={!visibilityEditable}
                      />
                    )}
                  </Stack>
                )}
                diffValue={
                  viewerIsContractor && product
                    ? product.texts?.clientTitleText
                    : undefined
                }
                DiffValueComponent={({ value }) => (
                  <Typography variant={"h4"}>{value}</Typography>
                )}
                showDiff={!!product}
                flex={1}
                placeholder={"-"}
              />
            </Grid>
          )}
          {((viewerIsContractor &&
            (item.productDescription ||
              product?.texts?.clientDescriptionText)) ||
            (item.productDescriptionClientVisibility !== "hide" &&
              item.productDescription)) && (
            <Grid size={2}>
              <ValueWithDiff
                value={item.productDescription}
                ValueComponent={({ value }) => (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <TruncatedHtmlValue
                      textProps={{
                        variant: "body2",
                        sx: {
                          minWidth: 0,
                          flex: 1,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "break-word",
                        },
                      }}
                      lines={5}
                      html={value}
                      showMoreLabel={t("Show more", { ns: "Global" })}
                      showLessLabel={t("Show less", { ns: "Global" })}
                      notSetLabel={t("Not set", { ns: "Global" })}
                    />
                    {viewerIsContractor && (
                      <VisibilityButton
                        visibility={item.productDescriptionClientVisibility}
                        onChange={visibility =>
                          setVisibility(
                            "productDescriptionClientVisibility",
                            visibility
                          )
                        }
                        disabled={!visibilityEditable}
                      />
                    )}
                  </Stack>
                )}
                diffValue={
                  viewerIsContractor
                    ? product?.texts?.clientDescriptionText
                    : undefined
                }
                DiffValueComponent={({ value }) => (
                  <TruncatedHtmlValue
                    textProps={{ variant: "body2" }}
                    lines={3}
                    html={value}
                    showMoreLabel={t("Show more", { ns: "Global" })}
                    showLessLabel={t("Show less", { ns: "Global" })}
                    notSetLabel={t("Not set", { ns: "Global" })}
                  />
                )}
                showDiff={!!product}
                flex={1}
                placeholder={"-"}
              />
            </Grid>
          )}

          {viewerIsContractor &&
            (item.productCategory || product?.category) && (
              <Grid size={1}>
                <Stack
                  direction={"row"}
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <LabeledValue
                    label={t("Category", {
                      ns: "Product",
                    })}
                  >
                    <ValueWithDiff
                      value={item.productCategory}
                      diffValue={
                        viewerIsContractor && product
                          ? product.category
                          : undefined
                      }
                      spacing={0}
                      showDiff={!!product}
                      placeholder={"-"}
                    />
                  </LabeledValue>
                  {viewerIsContractor && (
                    <VisibilityIndicatorIcon isVisible={false} />
                  )}
                </Stack>
              </Grid>
            )}
          {viewerIsContractor &&
            (item.productArticleNumber || product?.articleNumber) && (
              <Grid size={1}>
                <Stack
                  direction={"row"}
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {product &&
                  isEditable &&
                  productAlternatives &&
                  productAlternatives.propertyMatchingProductAlternatives
                    .length > 0 ? (
                    <RestrictedByCapabilityWithDebug capability="pim">
                      <SupplierProductPropertyMatchingProductAlternativesSelect
                        product={product}
                        propertyMatchingProductAlternatives={
                          productAlternatives.propertyMatchingProductAlternatives
                        }
                        onProductSelect={onProductSelect}
                      />
                    </RestrictedByCapabilityWithDebug>
                  ) : (
                    <LabeledValue
                      label={t("Article number", { ns: "Product" })}
                    >
                      <ValueWithDiff
                        value={item.productArticleNumber}
                        diffValue={
                          viewerIsContractor
                            ? product?.articleNumber
                            : undefined
                        }
                        showDiff={!!product}
                        spacing={0}
                        placeholder={"-"}
                      />
                    </LabeledValue>
                  )}
                  {viewerIsContractor && (
                    <VisibilityIndicatorIcon isVisible={false} />
                  )}
                </Stack>
              </Grid>
            )}
          {viewerIsContractor &&
            (item.productSupplier || product?.supplier) && (
              <Grid size={1}>
                <Stack
                  direction={"row"}
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <LabeledValue
                    label={t("Supplier", {
                      ns: "Product",
                    })}
                  >
                    <ValueWithDiff
                      value={item.productSupplier?.title}
                      diffValue={
                        viewerIsContractor
                          ? product?.supplier?.title
                          : undefined
                      }
                      showDiff={!!product}
                      spacing={0}
                      placeholder={"-"}
                    />
                  </LabeledValue>
                  {viewerIsContractor && (
                    <VisibilityIndicatorIcon isVisible={false} />
                  )}
                </Stack>
              </Grid>
            )}
          {viewerIsContractor && (item.productGtin || product?.gtin) && (
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <LabeledValue
                  label={t("GTIN", {
                    ns: "Product",
                  })}
                >
                  <ValueWithDiff
                    value={item.productGtin}
                    diffValue={viewerIsContractor ? product?.gtin : undefined}
                    showDiff={!!product}
                    spacing={0}
                    placeholder={"-"}
                  />
                </LabeledValue>
                {viewerIsContractor && (
                  <VisibilityIndicatorIcon isVisible={false} />
                )}
              </Stack>
            </Grid>
          )}
          {viewerIsContractor &&
            (item.productManufacturerArticleNumber ||
              product?.manufacturerArticleNumber) && (
              <Grid size={1}>
                <Stack
                  direction={"row"}
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <LabeledValue
                    label={t("Manufacturer article number", {
                      ns: "Product",
                    })}
                  >
                    <ValueWithDiff
                      value={item.productManufacturerArticleNumber}
                      diffValue={
                        viewerIsContractor
                          ? product?.manufacturerArticleNumber
                          : undefined
                      }
                      showDiff={!!product}
                      spacing={0}
                      placeholder={"-"}
                    />
                  </LabeledValue>
                  {viewerIsContractor && (
                    <VisibilityIndicatorIcon isVisible={false} />
                  )}
                </Stack>
              </Grid>
            )}
          {((viewerIsContractor &&
            (item.productBrand || product?.branding || product?.brand)) ||
            (item.productBrandClientVisibility !== "hide" &&
              item.productBrand)) && (
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <LabeledValue
                  label={t("Brand", {
                    ns: "Product",
                  })}
                >
                  <ValueWithDiff
                    value={item.productBrand}
                    diffValue={
                      viewerIsContractor
                        ? (product?.branding?.brand.title ??
                          product?.brand ??
                          undefined)
                        : undefined
                    }
                    showDiff={!!product}
                    spacing={0}
                    placeholder={"-"}
                  />
                </LabeledValue>
                {viewerIsContractor && (
                  <VisibilityButton
                    visibility={item.productBrandClientVisibility}
                    onChange={visibility =>
                      setVisibility("productBrandClientVisibility", visibility)
                    }
                    disabled={!visibilityEditable}
                  />
                )}
              </Stack>
            </Grid>
          )}
          {((viewerIsContractor &&
            (item.productBrandLine || product?.branding?.brandLine)) ||
            (item.productBrandClientVisibility !== "hide" &&
              item.productBrandLine)) && (
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <LabeledValue
                  label={t("Brand line", {
                    ns: "Product",
                  })}
                >
                  <ValueWithDiff
                    value={item.productBrandLine}
                    diffValue={
                      viewerIsContractor
                        ? product?.branding?.brandLine?.title
                        : undefined
                    }
                    showDiff={!!product}
                    spacing={0}
                    placeholder={"-"}
                  />
                </LabeledValue>
                {viewerIsContractor && (
                  <VisibilityButton
                    visibility={item.productBrandClientVisibility}
                    onChange={noop}
                    disabled={true}
                  />
                )}
              </Stack>
            </Grid>
          )}
          {((viewerIsContractor &&
            (item.productMainBrandLine ||
              product?.branding?.brandLine?.mainBrandLine)) ||
            (item.productBrandClientVisibility !== "hide" &&
              item.productMainBrandLine)) && (
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <LabeledValue
                  label={t("Main brand line", {
                    ns: "Product",
                  })}
                >
                  <ValueWithDiff
                    value={item.productMainBrandLine}
                    diffValue={
                      viewerIsContractor
                        ? product?.branding?.brandLine?.mainBrandLine?.title
                        : undefined
                    }
                    showDiff={!!product}
                    spacing={0}
                    placeholder={"-"}
                  />
                </LabeledValue>
                {viewerIsContractor && (
                  <VisibilityButton
                    visibility={item.productBrandClientVisibility}
                    onChange={noop}
                    disabled={true}
                  />
                )}
              </Stack>
            </Grid>
          )}
          {viewerIsContractor &&
            (item.productTypeTitle ||
              product?.referenceTyping?.productType?.label) && (
              <Grid size={1}>
                <Stack
                  direction={"row"}
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <LabeledValue
                    label={t("Product type", {
                      ns: "Product",
                    })}
                  >
                    <ValueWithDiff
                      value={item.productTypeTitle}
                      diffValue={
                        viewerIsContractor
                          ? product?.referenceTyping?.productType?.label
                          : undefined
                      }
                      showDiff={!!product}
                      spacing={0}
                      placeholder={"-"}
                    />
                  </LabeledValue>
                  {viewerIsContractor && (
                    <VisibilityIndicatorIcon isVisible={false} />
                  )}
                </Stack>
              </Grid>
            )}
        </Grid>
      </Box>
      <ProductPropertiesCollapseSection
        properties={properties}
        originalProperties={originalProperties}
        propertyAlternatives={
          isEditable ? productAlternatives?.propertyAlternatives : undefined
        }
        onProductSelect={isEditable ? onProductSelect : undefined}
        viewerIsContractor={viewerIsContractor}
        gridColumnSpacing={gridColumnSpacing}
        showInfo={viewMode === "info"}
      />
    </Stack>
  );
};

// Owns the expansion state so that it resets whenever the section unmounts
// (product type cleared, product removed) and, via the caller's key, when the
// product is replaced. The subtree mounts on first expansion only and stays
// mounted once collapsed, so a later expansion does not refetch.
const PlaceholderCollapseSection = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslate("QuoteItem");
  const [expanded, setExpanded] = React.useState(false);
  const [hasExpanded, setHasExpanded] = React.useState(false);

  return (
    <CollapseSection
      title={t("Product placeholder", { ns: "QuoteItem" })}
      titleVariant="h4"
      expanded={expanded}
      setExpanded={nextExpanded => {
        setExpanded(nextExpanded);
        if (nextExpanded) setHasExpanded(true);
      }}
      sx={{ pt: 1 }}
    >
      {hasExpanded && children}
    </CollapseSection>
  );
};

const EMPTY_ARRAY: any[] = [];

export function ProductSearchFiltersSection({
  projectId,
  docId,
  docType,
  item,
  productTypeId,
  isEditable = false,
  documentViewerPermissions,
  refetchWizardQuestions,
}: {
  projectId: string | null;
  docId: string;
  docType: DocType;
  item: ProductSearchFiltersSection_ItemFragment;
  productTypeId: string;
  isEditable: boolean;
  documentViewerPermissions: PermissionName[];
  refetchWizardQuestions: () => Promise<unknown>;
}) {
  const { t } = useTranslate([
    "QuoteItem",
    "Global",
    "Product",
    "ProductSearch",
    "ItemPropertyField",
  ]);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const renderProductExpressionGate = (
    gated: React.ReactNode,
    fallback: React.ReactNode
  ) => (
    <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
      capability={["pim", "expressions"]}
      permissionByType={EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE}
      document={document}
      otherwise={fallback}
    >
      {gated}
    </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
  );

  const filterOutOwnProductProperties = React.useMemo(
    () => isNotOwnProductPropertyOption(item.id),
    [item.id]
  );

  const canWriteExpressions = useCanWriteExpressions(document, ["pim"]);

  const client = useApolloClient();
  const [getProductType, productTypeQuery] =
    useProductSearchFiltersSection_ProductTypeLazyQuery({
      client,
      fetchPolicy: "network-only", //network-only here, because else we get problems with the product field!
    });
  React.useEffect(() => {
    if (productTypeId) {
      getProductType({
        variables: {
          productTypeId: productTypeId,
        },
      });
    }
  }, [getProductType, productTypeId]);
  const productType = getDataOrNull(productTypeQuery.data?.pimProductType);
  const productTypeSearchFilter =
    productType && productTypeId ? productType : null;
  // Property types the placeholder has no filter for yet — the picker's list.
  const unusedProductPropertyTypes = (
    productTypeSearchFilter?.propertyTypes ?? []
  ).filter(
    ptpt =>
      !item.productSearchFilterDefinitions.propertyFilters.some(
        f => f.key === ptpt.propertyType.key
      )
  );

  // The catalogue's actual brands / suppliers / price range for this product
  // type — the right value source for the named filters, unlike the property
  // ones which take their full set from the property definition.
  //
  // Scoped by product type and NOTHING else, deliberately: the facet answers
  // "what could this filter be set to", so narrowing it by the filters already
  // set would hide the very values the user is choosing between (picking one
  // brand would empty the brand list). This is where it differs from the
  // product-search modal, which narrows the facet by the whole live filter set.
  const availableFiltersQuery =
    useProductSearchFiltersSection_AvailableFiltersQuery({
      client,
      skip: !item.productPlaceholder,
      variables: { filters: productFilterFacetScope(item.productPlaceholder) },
    });
  const availableFilters = getDataOrNull(
    (availableFiltersQuery.data ?? availableFiltersQuery.previousData)
      ?.pimSearchProductsAvailableFilters
  );
  const brandOptions = (availableFilters?.brands ?? []).map(b => ({
    value: b.key,
    label: b.label,
  }));
  const brandLineOptions = (availableFilters?.brandLines ?? []).map(b => ({
    value: b.key,
    label: b.label,
  }));
  const supplierOptions = (availableFilters?.suppliers ?? []).map(s => ({
    value: s.supplier.id,
    label: s.supplier.title,
  }));

  // Everything the one picker can add: the named filters this placeholder does
  // not carry yet, then its product type's unused property types.
  const addableFilters: Array<{
    key: string;
    label: string;
    add: () => Promise<void>;
  }> = [
    ...(item.productSearchFilterDefinitions.brandFilter
      ? []
      : [
          {
            key: "brand",
            label: t("Brand", { ns: "Product" }),
            add: () =>
              writeFilters({
                brandFilter: {
                  textInFilter: { operatorTextIn: "in", valueTextIn: null },
                },
              }),
          },
        ]),
    ...(item.productSearchFilterDefinitions.brandLineFilter
      ? []
      : [
          {
            key: "brandLine",
            label: t("Brand line", { ns: "Product" }),
            add: () =>
              writeFilters({
                brandLineFilter: {
                  textInFilter: { operatorTextIn: "in", valueTextIn: null },
                },
              }),
          },
        ]),
    ...(item.productSearchFilterDefinitions.supplierIdFilter
      ? []
      : [
          {
            key: "supplier",
            label: t("Supplier", { ns: "Product" }),
            add: () =>
              writeFilters({
                supplierIdFilter: {
                  idInFilter: { operatorIdIn: "in", valueIdIn: [] },
                },
              }),
          },
        ]),
    ...(item.productSearchFilterDefinitions.listPriceFilter
      ? []
      : [
          {
            key: "listPrice",
            label: t("List price", { ns: "Product" }),
            add: () =>
              writeFilters({
                listPriceFilter: {
                  numberFilter: { operatorNumber: "lte", valueNumber: null },
                },
              }),
          },
        ]),
    ...(item.productSearchFilterDefinitions.favouritesFilter
      ? []
      : [
          {
            key: "favourites",
            label: t("Favourites", { ns: "ProductSearch" }),
            add: () =>
              writeFilters({
                favouritesFilter: {
                  boolFilter: { operatorBool: "eq", valueBool: true },
                },
              }),
          },
        ]),
    ...unusedProductPropertyTypes.map(ptpt => ({
      key: ptpt.propertyType.key,
      label: ptpt.propertyType.label,
      add: () =>
        setPropertyFilterSlot(
          ptpt.propertyType.key,
          newAuthoredProductFilterForPropertyType(ptpt.propertyType)
        ),
    })),
  ];

  const popularProductTypePropertyTypes =
    getDataOrNull(productTypeQuery.data?.pimProductTypePropertyTypeFavourites)
      ?.propertyTypeFavourites ?? EMPTY_ARRAY;

  const {
    supplierIdFilter: supplierSearchFilterDefinition,
    brandFilter: brandSearchFilterDefinition,
    brandLineFilter: brandLineSearchFilterDefinition,
    listPriceFilter: listPriceSearchFilterDefinition,
    propertyFilters: propertySearchFiltersDefinitions,
    favouritesFilter: organisationFavouritesSearchFilterDefinition,
  } = item.productSearchFilterDefinitions;

  const [setItemProductPlaceholder] =
    useProductBox_SetItemProductPlaceholderMutationWithWizardRefetch({
      client,
      refetchQueries: [namedOperations.Query.ProductBox_Product],
      refetchWizardQuestions,
    });
  const [removeItemProductPlaceholder] =
    useProductBox_RemoveItemProductPlaceholderMutationWithWizardRefetch({
      client,
      refetchQueries: [namedOperations.Query.ProductBox_Product],
      refetchWizardQuestions,
    });

  const {
    writeFilters,
    setPropertyFilterSlot,
    deletePropertyFilterSlot,
    defineAuthoredPropertyFilterSlots,
  } = useProductSearchFilterMutations({
    projectId,
    docId,
    itemId: item.id,
    definitions: item.productSearchFilterDefinitions,
    refetchQueries: [namedOperations.Query.ProductBox_Product],
  });

  const nonProductTypeSearchFiltersCount = [
    supplierSearchFilterDefinition,
    brandSearchFilterDefinition,
    brandLineSearchFilterDefinition,
    listPriceSearchFilterDefinition,
    ...propertySearchFiltersDefinitions,
  ].reduce((acc, cur) => (cur ? acc + 1 : acc), 0);
  // One merged array per context, so authored and expression-driven filters
  // are counted together.
  const searchFiltersCount = nonProductTypeSearchFiltersCount;
  // Clearing the product type wipes every slot, so a formula in any of them
  // makes that write need the expression access the resolver gates separately.
  const hasComputedSearchFilter =
    propertySearchFiltersDefinitions.some(isComputedSearchFilterSlot) ||
    [
      brandSearchFilterDefinition,
      brandLineSearchFilterDefinition,
      listPriceSearchFilterDefinition,
      organisationFavouritesSearchFilterDefinition,
    ].some(definition => definition?.__typename.endsWith("Computed") === true);
  const atLeastOneSearchFilterExists =
    searchFiltersCount +
      (organisationFavouritesSearchFilterDefinition ? 1 : 0) >
    0;
  // The stored product type counts even while the catalogue lookup is denied
  // or unresolved, so a read-only viewer still gets the question control.
  const atLeastOneFilterExists =
    searchFiltersCount +
      (organisationFavouritesSearchFilterDefinition ? 1 : 0) +
      (productTypeId ? 1 : 0) >
    0;

  const supplierSearchFilterDefinitionIds: string[] =
    supplierSearchFilterDefinition
      ? supplierSearchFilterDefinition.__typename === "EntitySearchIdFilter"
        ? [supplierSearchFilterDefinition.valueId]
        : supplierSearchFilterDefinition.valueIdIn
      : [];

  const suppliersQuery = useProductBox_SuppliersQuery({
    client,
    variables: {
      pimSupplierIds: supplierSearchFilterDefinitionIds,
    },
    fetchPolicy: "cache-and-network",
    skip: !supplierSearchFilterDefinitionIds.length,
  });
  const getSupplierName = (id: string) => {
    const r = getDataOrNull(
      (suppliersQuery.data ?? suppliersQuery.previousData)?.pimSearchSuppliers
    )?.suppliers.find(s => s.id === id);
    return r?.title;
  };

  // Every filter the section shows, for the edit modal: what each compares,
  // and the two edits that belong to the filter rather than to its value.
  // The named filters offer no operator choice — their clause kind follows the
  // value, one brand being an equality and several a membership.
  const yesLabel = t("Yes", { ns: "Global" });
  const noLabel = t("No", { ns: "Global" });
  const filterRows: SearchFilterOperatorRow[] = [
    ...(organisationFavouritesSearchFilterDefinition
      ? [
          {
            key: "favourites",
            label: t("Favourites", { ns: "ProductSearch" }),
            value: searchFilterValueText(
              organisationFavouritesSearchFilterDefinition,
              { boolLabels: { yes: yesLabel, no: noLabel } }
            ),
            isComputed:
              organisationFavouritesSearchFilterDefinition.__typename.endsWith(
                "Computed"
              ),
            operator: "eq" as const,
            operatorOptions: BoolFilterOperatorOptions,
            onDelete: () => writeFilters({ favouritesFilter: null }),
          },
        ]
      : []),
    ...(supplierSearchFilterDefinition
      ? [
          {
            key: "supplier",
            label: t("Supplier", { ns: "Product" }),
            value:
              filterNonNull(
                supplierSearchFilterDefinitionIds.map(getSupplierName)
              ).join(", ") || "-",
            isComputed: false,
            operator:
              supplierSearchFilterDefinition.__typename ===
              "EntitySearchIdInFilter"
                ? ("in" as const)
                : ("eq" as const),
            operatorOptions: [],
            onDelete: () => writeFilters({ supplierIdFilter: null }),
          },
        ]
      : []),
    ...(brandSearchFilterDefinition
      ? [
          {
            key: "brand",
            label: t("Brand", { ns: "Product" }),
            value: searchFilterValueText(brandSearchFilterDefinition),
            isComputed:
              brandSearchFilterDefinition.__typename.endsWith("Computed"),
            operator: brandSearchFilterDefinition.__typename.startsWith(
              "EntitySearchTextIn"
            )
              ? ("in" as const)
              : ("eq" as const),
            operatorOptions: TextFilterOperatorOptions,
            onDelete: () => writeFilters({ brandFilter: null }),
          },
        ]
      : []),
    ...(brandLineSearchFilterDefinition
      ? [
          {
            key: "brandLine",
            label: t("Brand line", { ns: "Product" }),
            value: searchFilterValueText(brandLineSearchFilterDefinition),
            isComputed:
              brandLineSearchFilterDefinition.__typename.endsWith("Computed"),
            operator: brandLineSearchFilterDefinition.__typename.startsWith(
              "EntitySearchTextIn"
            )
              ? ("in" as const)
              : ("eq" as const),
            operatorOptions: TextFilterOperatorOptions,
            onDelete: () => writeFilters({ brandLineFilter: null }),
          },
        ]
      : []),
    ...(listPriceSearchFilterDefinition
      ? [
          {
            key: "listPrice",
            label: t("List price", { ns: "Product" }),
            value: searchFilterValueText(listPriceSearchFilterDefinition),
            isComputed:
              listPriceSearchFilterDefinition.__typename.endsWith("Computed"),
            operator:
              "operatorNumberBetween" in listPriceSearchFilterDefinition
                ? listPriceSearchFilterDefinition.operatorNumberBetween
                : listPriceSearchFilterDefinition.operatorNumber,
            operatorOptions: PriceFilterOperatorOptions,
            onDelete: () => writeFilters({ listPriceFilter: null }),
          },
        ]
      : []),
    ...propertySearchFiltersDefinitions.map(definition => {
      const propertyType = productTypeSearchFilter?.propertyTypes.find(
        p => p.propertyType.key === definition.key
      )?.propertyType;
      return {
        key: definition.key,
        label: propertyType ? propertyType.label : definition.key,
        value: searchFilterValueText(definition, {
          unit: propertyType?.kind === "number" ? propertyType.unit : undefined,
          boolLabels: { yes: yesLabel, no: noLabel },
        }),
        isComputed: isComputedSearchFilterSlot(definition),
        operator: searchFilterOperator(definition),
        operatorOptions: searchFilterOperatorOptions(definition),
        onChangeOperator: (operator: EntitySearchFilterOperator) =>
          setPropertyFilterSlot(
            definition.key,
            searchFilterSlotWithOperator(definition, operator)
          ),
        onDelete: () => deletePropertyFilterSlot(definition.key),
      };
    }),
  ];

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  if (!(isEditable || atLeastOneFilterExists)) return null;

  const gridColumnSpacing = 2;

  const handleClearProductType = async () => {
    if (nonProductTypeSearchFiltersCount > 0) {
      const result = await startConfirmProcess({
        title: t("All filters and expressions will be deleted", {
          ns: "Product",
        }),
        notInStack: true,
      });
      if (!result) return;
    }
    // Removing the placeholder takes its filters and their expressions along.
    await removeItemProductPlaceholder({
      variables: { input: { projectId, docId, itemId: item.id } },
    });
  };

  // The backend drops the property filters and expression-driven slots of the
  // old type and keeps the authored named filters.
  const handleSelectProductType = async (
    productType: ProductTypesSearchModal_PimProductTypeFragment
  ) => {
    await setItemProductPlaceholder({
      variables: {
        input: {
          projectId,
          docId,
          itemId: item.id,
          productTypeId: productType.id,
        },
      },
    });
  };

  return (
    <Stack direction="column" spacing={1}>
      <ConfirmProcess ref={confirmProcessRef} />
      {productTypeId && (
        <ProductTypeInput
          productTypeId={productTypeId}
          productTypeSystem="REFERENCE"
          onClear={
            hasComputedSearchFilter && !canWriteExpressions
              ? undefined
              : handleClearProductType
          }
          onChange={handleSelectProductType}
          canEdit={isEditable}
          renderEditIconButton={button => (
            <RestrictedByCapabilityWithDebug capability="pim">
              {button}
            </RestrictedByCapabilityWithDebug>
          )}
          renderAddButton={button => (
            <RestrictedByCapabilityWithDebug capability="pim">
              {button}
            </RestrictedByCapabilityWithDebug>
          )}
        />
      )}
      <CollapseSection
        title={t("Search filters", { ns: "Product" })}
        titleVariant="h4"
        isInitiallyExpanded={true}
        hideItemCountOnExpand={true}
        itemCount={searchFiltersCount}
        ActionButtons={
          <Stack direction={"row"} spacing={1}>
            {/* Disabled rather than unmounted while empty: the button owns the
                open modal, which must survive deleting the last row. */}
            {isEditable && (
              <RestrictedByCapabilityWithDebug capability="pim">
                <ModalOpenButton
                  Modal={SearchFilterOperatorsModal}
                  modalProps={{
                    rows: filterRows,
                    renderExpressionGate: renderProductExpressionGate,
                  }}
                >
                  <IconButton
                    color={"primary"}
                    size={"small"}
                    disabled={filterRows.length === 0}
                    aria-label={t("Edit search filters", {
                      ns: "QuoteItem",
                    })}
                  >
                    <EditIcon />
                  </IconButton>
                </ModalOpenButton>
              </RestrictedByCapabilityWithDebug>
            )}
            {isEditable && addableFilters.length > 0 && (
              <RestrictedByCapabilityWithDebug capability="pim">
                <MenuButton
                  Icon={<AddIcon />}
                  buttonProps={{
                    "aria-label": t("Add search filter", { ns: "QuoteItem" }),
                  }}
                >
                  {/* One picker for every addable filter — named ones and
                      property ones alike. A filter is created without a value;
                      the row then takes either a value or a formula. */}
                  {addableFilters.map(addable => (
                    <MenuItem
                      key={addable.key}
                      onClick={() => void addable.add()}
                    >
                      {addable.label}
                    </MenuItem>
                  ))}
                </MenuButton>
              </RestrictedByCapabilityWithDebug>
            )}
          </Stack>
        }
      >
        <Grid
          container
          columns={2}
          rowSpacing={1}
          columnSpacing={gridColumnSpacing}
        >
          {organisationFavouritesSearchFilterDefinition && (
            <Grid size={1}>
              <SearchFilterField
                projectId={projectId}
                docId={docId}
                itemId={item.id}
                filterOptions={filterOutOwnProductProperties}
                label={t("Favourites", { ns: "ProductSearch" })}
                value={getValueWithOperator(
                  organisationFavouritesSearchFilterDefinition
                )}
                isComputed={organisationFavouritesSearchFilterDefinition.__typename.endsWith(
                  "Computed"
                )}
                expression={
                  "expr" in organisationFavouritesSearchFilterDefinition
                    ? organisationFavouritesSearchFilterDefinition.expr
                    : ""
                }
                operator="eq"
                operatorOptions={BoolFilterOperatorOptions}
                readOnly={!isEditable}
                valueInput={
                  <NamedBoolFilterInput
                    label={t("Favourites", { ns: "ProductSearch" })}
                    disabled={!isEditable}
                    value={
                      organisationFavouritesSearchFilterDefinition.__typename ===
                      "EntitySearchBoolFilter"
                        ? (organisationFavouritesSearchFilterDefinition.valueBool ??
                          null)
                        : null
                    }
                    onChange={async value => {
                      await writeFilters({
                        favouritesFilter: {
                          boolFilter: { operatorBool: "eq", valueBool: value },
                        },
                      });
                    }}
                  />
                }
                onSaveExpression={async (_operator, expr) => {
                  await writeFilters({
                    favouritesFilter: {
                      boolFilterComputed: { operatorBool: "eq", expr },
                    },
                  });
                }}
                onRemoveExpression={async () => {
                  await writeFilters({
                    favouritesFilter: {
                      boolFilter: {
                        operatorBool: "eq",
                        valueBool:
                          "missingValue" in
                            organisationFavouritesSearchFilterDefinition &&
                          !organisationFavouritesSearchFilterDefinition.missingValue
                            ? (organisationFavouritesSearchFilterDefinition.valueBoolComputed ??
                              null)
                            : null,
                      },
                    },
                  });
                }}
                renderExpressionGate={renderProductExpressionGate}
              />
            </Grid>
          )}
          {supplierSearchFilterDefinition && (
            <Grid size={1}>
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* An Id filter: no computed counterpart, so no formula. */}
                <NamedMultiSelectFilterInput
                  label={t("Supplier", { ns: "Product" })}
                  disabled={!isEditable}
                  options={supplierOptions}
                  getLabelForValue={id => getSupplierName(id) ?? undefined}
                  value={
                    supplierSearchFilterDefinition.__typename ===
                    "EntitySearchIdFilter"
                      ? [supplierSearchFilterDefinition.valueId]
                      : supplierSearchFilterDefinition.valueIdIn
                  }
                  onChange={async ids => {
                    await writeFilters({
                      supplierIdFilter:
                        ids.length === 1
                          ? { idFilter: { operatorId: "eq", valueId: ids[0] } }
                          : {
                              idInFilter: {
                                operatorIdIn: "in",
                                valueIdIn: ids,
                              },
                            },
                    });
                  }}
                />
              </Stack>
            </Grid>
          )}
          {brandSearchFilterDefinition && (
            <Grid size={1}>
              <SearchFilterField
                projectId={projectId}
                docId={docId}
                itemId={item.id}
                filterOptions={filterOutOwnProductProperties}
                label={t("Brand", { ns: "Product" })}
                value={getValueWithOperator(brandSearchFilterDefinition)}
                isComputed={brandSearchFilterDefinition.__typename.endsWith(
                  "Computed"
                )}
                expression={
                  "expr" in brandSearchFilterDefinition
                    ? brandSearchFilterDefinition.expr
                    : ""
                }
                operator={
                  brandSearchFilterDefinition.__typename.startsWith(
                    "EntitySearchTextIn"
                  )
                    ? "in"
                    : "eq"
                }
                operatorOptions={TextFilterOperatorOptions}
                readOnly={!isEditable}
                valueInput={
                  <NamedMultiSelectFilterInput
                    label={t("Brand", { ns: "Product" })}
                    disabled={!isEditable}
                    options={brandOptions}
                    value={
                      brandSearchFilterDefinition.__typename ===
                      "EntitySearchTextFilter"
                        ? brandSearchFilterDefinition.valueText
                          ? [brandSearchFilterDefinition.valueText]
                          : []
                        : brandSearchFilterDefinition.__typename ===
                            "EntitySearchTextInFilter"
                          ? (brandSearchFilterDefinition.valueTextIn ?? [])
                          : []
                    }
                    onChange={async values => {
                      await writeFilters({
                        brandFilter:
                          values.length === 1
                            ? {
                                textFilter: {
                                  operatorText: "eq",
                                  valueText: values[0],
                                },
                              }
                            : {
                                textInFilter: {
                                  operatorTextIn: "in",
                                  valueTextIn: values,
                                },
                              },
                      });
                    }}
                  />
                }
                onSaveExpression={async (operator, expr) => {
                  await writeFilters({
                    brandFilter:
                      operator === "in"
                        ? {
                            textInFilterComputed: {
                              operatorTextIn: operator,
                              expr,
                            },
                          }
                        : {
                            textFilterComputed: { operatorText: "eq", expr },
                          },
                  });
                }}
                onRemoveExpression={async () => {
                  await writeFilters({
                    brandFilter: bareTextFilterWithoutExpression(
                      brandSearchFilterDefinition
                    ),
                  });
                }}
                renderExpressionGate={renderProductExpressionGate}
              />
            </Grid>
          )}
          {brandLineSearchFilterDefinition && (
            <Grid size={1}>
              <SearchFilterField
                projectId={projectId}
                docId={docId}
                itemId={item.id}
                filterOptions={filterOutOwnProductProperties}
                label={t("Brand line", { ns: "Product" })}
                value={getValueWithOperator(brandLineSearchFilterDefinition)}
                isComputed={brandLineSearchFilterDefinition.__typename.endsWith(
                  "Computed"
                )}
                expression={
                  "expr" in brandLineSearchFilterDefinition
                    ? brandLineSearchFilterDefinition.expr
                    : ""
                }
                operator={
                  brandLineSearchFilterDefinition.__typename.startsWith(
                    "EntitySearchTextIn"
                  )
                    ? "in"
                    : "eq"
                }
                operatorOptions={TextFilterOperatorOptions}
                readOnly={!isEditable}
                valueInput={
                  <NamedMultiSelectFilterInput
                    label={t("Brand line", { ns: "Product" })}
                    disabled={!isEditable}
                    options={brandLineOptions}
                    value={
                      brandLineSearchFilterDefinition.__typename ===
                      "EntitySearchTextFilter"
                        ? brandLineSearchFilterDefinition.valueText
                          ? [brandLineSearchFilterDefinition.valueText]
                          : []
                        : brandLineSearchFilterDefinition.__typename ===
                            "EntitySearchTextInFilter"
                          ? (brandLineSearchFilterDefinition.valueTextIn ?? [])
                          : []
                    }
                    onChange={async values => {
                      await writeFilters({
                        brandLineFilter:
                          values.length === 1
                            ? {
                                textFilter: {
                                  operatorText: "eq",
                                  valueText: values[0],
                                },
                              }
                            : {
                                textInFilter: {
                                  operatorTextIn: "in",
                                  valueTextIn: values,
                                },
                              },
                      });
                    }}
                  />
                }
                onSaveExpression={async (operator, expr) => {
                  await writeFilters({
                    brandLineFilter:
                      operator === "in"
                        ? {
                            textInFilterComputed: {
                              operatorTextIn: operator,
                              expr,
                            },
                          }
                        : {
                            textFilterComputed: { operatorText: "eq", expr },
                          },
                  });
                }}
                onRemoveExpression={async () => {
                  await writeFilters({
                    brandLineFilter: bareTextFilterWithoutExpression(
                      brandLineSearchFilterDefinition
                    ),
                  });
                }}
                renderExpressionGate={renderProductExpressionGate}
              />
            </Grid>
          )}
          {listPriceSearchFilterDefinition && (
            <Grid size={1}>
              <SearchFilterField
                projectId={projectId}
                docId={docId}
                itemId={item.id}
                filterOptions={filterOutOwnProductProperties}
                label={t("List price", { ns: "Product" })}
                value={getValueWithOperator(listPriceSearchFilterDefinition)}
                isComputed={listPriceSearchFilterDefinition.__typename.endsWith(
                  "Computed"
                )}
                expression={
                  "expr" in listPriceSearchFilterDefinition
                    ? listPriceSearchFilterDefinition.expr
                    : ""
                }
                operator={
                  "operatorNumberBetween" in listPriceSearchFilterDefinition
                    ? listPriceSearchFilterDefinition.operatorNumberBetween
                    : listPriceSearchFilterDefinition.operatorNumber
                }
                operatorOptions={PriceFilterOperatorOptions}
                readOnly={!isEditable}
                valueInput={
                  <NamedRangeFilterInput
                    label={t("List price", { ns: "Product" })}
                    disabled={!isEditable}
                    min={priceBound(listPriceSearchFilterDefinition, "min")}
                    max={priceBound(listPriceSearchFilterDefinition, "max")}
                    onChange={async range => {
                      await writeFilters({
                        listPriceFilter:
                          range.min !== null && range.max !== null
                            ? {
                                numberBetweenFilter: {
                                  operatorNumberBetween: "between",
                                  valueNumberBetween: {
                                    min: range.min,
                                    max: range.max,
                                  },
                                },
                              }
                            : {
                                numberFilter: {
                                  operatorNumber: oneSidedPriceOperator(
                                    listPriceSearchFilterDefinition,
                                    range.max !== null ? "max" : "min"
                                  ),
                                  valueNumber: range.max ?? range.min,
                                },
                              },
                      });
                    }}
                  />
                }
                onSaveExpression={async (operator, expr) => {
                  await writeFilters({
                    listPriceFilter:
                      operator === "between"
                        ? {
                            numberBetweenFilterComputed: {
                              operatorNumberBetween: operator,
                              expr,
                            },
                          }
                        : {
                            numberFilterComputed: {
                              operatorNumber: numberFilterOperator(operator),
                              expr,
                            },
                          },
                  });
                }}
                onRemoveExpression={async () => {
                  await writeFilters({
                    listPriceFilter: barePriceFilterWithoutExpression(
                      listPriceSearchFilterDefinition
                    ),
                  });
                }}
                renderExpressionGate={renderProductExpressionGate}
              />
            </Grid>
          )}

          {propertySearchFiltersDefinitions.map(definition => {
            const productTypePropertyType =
              productTypeSearchFilter?.propertyTypes.find(
                p => p.propertyType.key === definition.key
              );
            const propertyType = productTypePropertyType?.propertyType;
            const propertyTypeValues =
              productTypePropertyType?.propertyTypeValues;
            return (
              <Grid key={definition.key} size={1}>
                <SearchFilterField
                  projectId={projectId}
                  docId={docId}
                  itemId={item.id}
                  filterOptions={filterOutOwnProductProperties}
                  label={propertyType ? propertyType.label : definition.key}
                  {...searchFilterFieldPropsForSlot(
                    definition,
                    propertyType?.kind === "number"
                      ? propertyType.unit
                      : undefined
                  )}
                  valueInput={
                    <SearchFilterValueInput
                      slot={definition}
                      label={propertyType ? propertyType.label : definition.key}
                      valueOptions={searchFilterValueOptionsFromPimPropertyType(
                        productTypePropertyType
                      )}
                      disabled={!isEditable}
                      buildEntry={searchFilterEntry}
                      onChange={entry =>
                        setPropertyFilterSlot(definition.key, entry)
                      }
                    />
                  }
                  readOnly={!isEditable}
                  onSaveExpression={(operator, expr) =>
                    setPropertyFilterSlot(
                      definition.key,
                      searchFilterSlotWithExpression(definition, operator, expr)
                    )
                  }
                  onRemoveExpression={() =>
                    setPropertyFilterSlot(
                      definition.key,
                      searchFilterSlotWithoutExpression(definition)
                    )
                  }
                  renderExpressionGate={renderProductExpressionGate}
                />
              </Grid>
            );
          })}
        </Grid>
      </CollapseSection>
      <ProductSelectQuestionControlForm
        projectId={projectId}
        docId={docId}
        docType={docType}
        item={item}
        canEdit={isEditable}
        refetchWizardQuestions={refetchWizardQuestions}
      />
    </Stack>
  );
}

function ProductSelectQuestionControlForm({
  projectId,
  docId,
  docType,
  item,
  canEdit = true,
  refetchWizardQuestions,
}: {
  projectId: string | null;
  docId: string;
  docType: DocType;
  item: ProductSelectQuestionControlForm_ItemFragment;
  canEdit?: boolean;
  refetchWizardQuestions: () => Promise<unknown>;
}) {
  const client = useApolloClient();
  const [modifyWizardSettings] =
    useProductSelectQuestionControlForm_ModifyWizardSettingsMutationWithWizardRefetch(
      {
        client,
        refetchWizardQuestions,
      }
    );

  return (
    <Formik<QuestionControlSectionFormValues>
      initialValues={{
        questionControl: item.wizardSettings.paidAndProductQuestionControl,
        // askWhen:
        //   item.wizardSettings.paidAndProductQuestionControl.at(0)?.askWhen ??
        //   [],
        // askWhom:
        //   item.wizardSettings.paidAndProductQuestionControl.at(0)?.askWhom ??
        //   [],
        prompt: item.wizardSettings.promptPaidAndProduct,
      }}
      onSubmit={async values => {
        await modifyWizardSettings({
          variables: {
            input: {
              projectId,
              docId,
              itemId: item.id,
              values: {
                paidAndProductQuestionControl: values.questionControl,
                // paidAndProductQuestionControl: [
                //   { askWhen: values.askWhen, askWhom: values.askWhom },
                // ],
                promptPaidAndProduct: values.prompt,
              },
            },
          },
        });
      }}
    >
      <RestrictedByCapabilityWithDebug
        capability="pim"
        otherwise={
          <QuestionControlSection
            isInitiallyExpanded
            disabled={true}
            readOnly={true}
          />
        }
      >
        <Form>
          <QuestionControlSection
            readOnly={!canEdit}
            isInitiallyExpanded
            disabled={false}
          />
          <AutoSave />
        </Form>
      </RestrictedByCapabilityWithDebug>
    </Formik>
  );
}

/** Narrow an operator choice to the plain number-filter operators. */
// A value-only edit of a one-sided bound keeps a stored strict operator on
// the same side; anything else becomes the inclusive bound.
function oneSidedPriceOperator(
  filter: NonNullable<
    ItemProductSearchFilterDefinitionsFragment["listPriceFilter"]
  >,
  bound: "min" | "max"
): EntitySearchNumberFilterOperator {
  const stored = "operatorNumber" in filter ? filter.operatorNumber : null;
  if (bound === "max") return stored === "lt" ? "lt" : "lte";
  return stored === "gt" ? "gt" : "gte";
}

function numberFilterOperator(
  operator: EntitySearchFilterOperator
): EntitySearchNumberFilterOperator {
  return operator === "eq" ||
    operator === "gt" ||
    operator === "gte" ||
    operator === "lt" ||
    operator === "lte"
    ? operator
    : "eq";
}
